/**
 * Aus einem Leed einen Kunden machen — direkt im Nachrichtenfenster (Benjamin,
 * 23.09.2026: „es muss einfach sein, den Kunden in die Stammdaten zu übernehmen").
 *
 * Vorbelegt ist, was der Import aus der ersten Mail gelesen hat; was dort
 * fehlt, sucht `useVerlaufFund` im ganzen Verlauf (Telefon, Adresse — Benjamin,
 * 03.10.2026). Vorschläge aus einem Fremdtext, deshalb alles änderbar. Ein Projekt ist freiwillig:
 * ohne Titel entsteht nur der Kunde.
 *
 * Steht der Absender schon im Kundenstamm — gleiche oder fast gleiche Mail,
 * Telefon, Name, Anschrift —, steht er oben als Vorschlag (`KundenKandidaten`,
 * 26.10.2026), und neu angelegt wird nur noch bewusst. Bei gleicher Mailadresse
 * legt der Server gar nicht neu an. Der Abschluss sagt, was wirklich geschah:
 * zugeordnet oder neu angelegt, und was mit der Mailadresse passiert ist.
 */
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { ApiError, fehlerText } from "@vimaana/ui";

import type { Faden, LeedKurz } from "../../hooks/useNachrichten";
import { useUpdateKunde } from "../../hooks/usePlanung";
import { useLeedKandidaten, useLeedPatch, useLeedUmwandeln, type LeedKandidat } from "../../hooks/usePost";
import { neuerKundeName, useVerlaufFund } from "../../hooks/useVerlauf";
import { kundenName } from "../../lib/anrede";
import AnredeFeld from "../kunden/AnredeFeld";
import KundenKandidaten, { kundeWort, type MailWahl } from "./KundenKandidaten";

type Props = { leed: LeedKurz; faden: Faden; vorschlagTitel: string; onClose: () => void; onFertig: () => void };
type Feld = "name" | "telefon" | "strasse" | "plz" | "ort";
type Ergebnis = { neu: boolean; name: string; anrede: string; projekt: boolean; mail: "" | "ersetzt" | "dazu" };

const FELDER: { id: Feld; label: string; art?: string }[] = [
  { id: "name", label: "Name" },
  { id: "telefon", label: "Telefon", art: "tel" },
  { id: "strasse", label: "Straße" },
  { id: "plz", label: "PLZ" },
  { id: "ort", label: "Ort" },
];

function abschluss(e: Ergebnis, mail: string): string {
  const projekt = e.projekt ? " Das Projekt ist angelegt." : "";
  if (e.neu) return `${e.name || "Der Kunde"} ist neu im Kundenstamm angelegt. Der Schriftwechsel hängt jetzt dort.${projekt}`;
  const mailSatz = e.mail === "ersetzt" ? ` Die Mailadresse ist jetzt ${mail}.`
    : e.mail === "dazu" ? ` ${mail} steht dort als weitere Mailadresse.` : "";
  return `Zugeordnet: Der Schriftwechsel hängt jetzt an der bestehenden ${kundeWort(e.anrede)} ${e.name}. Neu angelegt wurde niemand.${mailSatz}${projekt}`;
}

export default function LeedUebernehmen({ leed, faden, vorschlagTitel, onClose, onFertig }: Props) {
  const qc = useQueryClient();
  const patch = useLeedPatch();
  const umwandeln = useLeedUmwandeln();
  const kandidatenQ = useLeedKandidaten(leed.id);
  const fund = useVerlaufFund(faden);
  // Die Adresse nur als Ganzes aus dem Verlauf — nie Straße von hier, Ort von dort.
  const adresseAusVerlauf = !leed.strasse && !!fund.strasse;
  const ergaenzt = [!leed.telefon && fund.telefon ? "Telefon" : "", adresseAusVerlauf ? "Adresse" : ""].filter(Boolean);
  const [werte, setWerte] = useState<Record<Feld, string>>({
    name: leed.name, telefon: leed.telefon || fund.telefon,
    strasse: adresseAusVerlauf ? fund.strasse : leed.strasse,
    plz: adresseAusVerlauf ? fund.plz : leed.plz,
    ort: adresseAusVerlauf ? fund.ort : leed.ort,
  });
  const [typ, setTyp] = useState<"privat" | "firma">(leed.kundentyp === "firma" ? "firma" : "privat");
  // Privatkunde: Anrede, Vor- und Nachname wie in der Kundenmaske (Benjamin, 04.10.2026:
  // „die Anrede muss noch abgefragt werden"). Vorschlag aus unserer eigenen Begrüßung.
  const [person, setPerson] = useState(() => neuerKundeName(faden, leed.name, fund));
  const kundeAendern = useUpdateKunde();
  const name = typ === "privat" ? kundenName(person.anrede, person.vorname, person.nachname) : werte.name;
  const [mitProjekt, setMitProjekt] = useState(false);
  const [titel, setTitel] = useState(vorschlagTitel.replace(/^(re|aw|fwd?|wg)\s*:\s*/i, "").slice(0, 80));
  // Was der Server beim Anlegen zusätzlich gefunden hat (409) — ersetzt die Vorabsuche.
  const [nachgereicht, setNachgereicht] = useState<LeedKandidat[]>([]);
  const [fertig, setFertig] = useState<Ergebnis | null>(null);

  useEffect(() => {
    const zu = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", zu);
    return () => window.removeEventListener("keydown", zu);
  }, [onClose]);

  const kandidaten = nachgereicht.length ? nachgereicht : kandidatenQ.data ?? [];
  const hart = kandidaten.some((k) => k.hart);
  const sucht = kandidatenQ.isPending;
  const laeuft = patch.isPending || umwandeln.isPending;
  const fehler = patch.error || (umwandeln.error && !(umwandeln.error instanceof ApiError && umwandeln.error.status === 409) ? umwandeln.error : null);

  const los = (ziel: LeedKandidat | null, mail: MailWahl = "") => {
    const kundeId = ziel?.id || "";
    const geaendert: Record<string, string> = {};
    const jetzt: Record<Feld, string> = { ...werte, name };
    for (const f of FELDER) if ((jetzt[f.id] || "").trim() !== (leed[f.id] || "").trim()) geaendert[f.id] = jetzt[f.id].trim();
    if (typ !== (leed.kundentyp || "privat")) geaendert.kundentyp = typ;
    const umwandelnJetzt = () => umwandeln.mutate(
      {
        id: leed.id,
        body: {
          kunde_id: kundeId, projekt_titel: mitProjekt ? titel.trim() : "", projekt_ort: werte.ort.trim(), projektart: "",
          mail: kundeId ? mail : "",
          // Wer die Vorschläge gesehen hat und trotzdem „neu" drückt, meint es so.
          trotzdem_neu: !kundeId && kandidaten.length > 0,
        },
      },
      {
        onSuccess: (r) => {
          // Die Anrede kennt das Leed nicht — sie geht gleich in den neuen Kundenstamm.
          if (typ === "privat" && !kundeId) kundeAendern.mutate({ id: r.kunde_id, patch: { ...person, name } });
          setFertig({
            neu: r.neu, name: ziel ? ziel.name : name, anrede: ziel ? ziel.anrede : person.anrede,
            projekt: !!r.projekt_id, mail: r.mail,
          });
          qc.invalidateQueries({ queryKey: ["nachrichten"] });
        },
        onError: (e) => {
          // 409 = der Server kennt passende Kunden (gleiche Mail sperrt, Ähnliches fragt nach).
          const d = e instanceof ApiError ? (e.body as { detail?: { kunden?: LeedKandidat[] } })?.detail : null;
          if (d && typeof d === "object" && Array.isArray(d.kunden)) setNachgereicht(d.kunden);
        },
      },
    );
    if (Object.keys(geaendert).length && !kundeId) patch.mutate({ id: leed.id, felder: geaendert }, { onSuccess: umwandelnJetzt });
    else umwandelnJetzt();
  };

  return (
    <div className="nb-dialog-schleier" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <section className="nb-dialog" role="dialog" aria-modal="true" aria-label="Als Kunde übernehmen">
        <header className="nb-dialog-kopf">
          <h3>Als Kunde übernehmen</h3>
          <button type="button" className="nb-zu" aria-label="Schließen" onClick={onClose}>×</button>
        </header>

        {fertig ? (
          <div className="nb-dialog-inhalt">
            <p>{abschluss(fertig, leed.email)}</p>
            <div className="nb-dialog-knoepfe">
              <button type="button" className="vg-btn vg-btn-primary" onClick={onFertig}>Fertig</button>
            </div>
          </div>
        ) : (
          <div className="nb-dialog-inhalt">
            <p className="nb-dialog-mail">{leed.email}</p>
            {sucht && <p className="nb-dialog-mail">Suche im Kundenstamm …</p>}
            <KundenKandidaten kandidaten={kandidaten} leedMail={leed.email} disabled={laeuft} onZuordnen={(k, mail) => los(k, mail)} />
            {kandidaten.length > 0 && !hart && <p className="nb-dialog-zwischen">Nicht dabei? Dann neu anlegen:</p>}

            {!hart && (
              <>
                {ergaenzt.length > 0 && <p className="nb-dialog-mail">{ergaenzt.join(" und ")} aus dem Mailverlauf ergänzt — bitte prüfen.</p>}
                <div className="nz-segmente" role="group" aria-label="Kundentyp">
                  {(["privat", "firma"] as const).map((t) => (
                    <button key={t} type="button" className={"nz-seg" + (typ === t ? " is-aktiv" : "")} aria-pressed={typ === t} onClick={() => setTyp(t)}>
                      {t === "privat" ? "Privat" : "Firma"}
                    </button>
                  ))}
                </div>
                <div className="nb-dialog-felder">
                  {typ === "privat" && <div className="nb-feld ist-name"><AnredeFeld {...person} onChange={setPerson} /></div>}
                  {FELDER.filter((f) => typ === "firma" || f.id !== "name").map((f) => (
                    <label key={f.id} className={`nb-feld ist-${f.id}`}>
                      <span>{f.label}</span>
                      <input type={f.art || "text"} value={werte[f.id]} onChange={(e) => setWerte({ ...werte, [f.id]: e.target.value })} />
                    </label>
                  ))}
                </div>
              </>
            )}
            <label className="nb-haken">
              <input type="checkbox" checked={mitProjekt} onChange={(e) => setMitProjekt(e.target.checked)} /> Gleich ein Projekt anlegen
            </label>
            {mitProjekt && (
              <label className="nb-feld">
                <span>Projekttitel</span>
                <input type="text" value={titel} onChange={(e) => setTitel(e.target.value)} placeholder="z. B. Teich anlegen" />
              </label>
            )}

            {fehler && <p className="vg-post-fehler">{fehlerText(fehler)}</p>}
            <div className="nb-dialog-knoepfe">
              <button type="button" className="vg-btn vg-btn-ghost" onClick={onClose} disabled={laeuft}>Abbrechen</button>
              {!hart && (
                <button
                  type="button" className={"vg-btn " + (kandidaten.length ? "" : "vg-btn-primary")}
                  disabled={laeuft || sucht || !name.trim() || (mitProjekt && !titel.trim())}
                  onClick={() => los(null)}
                >
                  {laeuft ? "Wird gespeichert …" : (kandidaten.length ? "Trotzdem neu anlegen" : "Neu anlegen") + (mitProjekt ? " mit Projekt" : "")}
                </button>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
